import {
  CAPABILITY_APM,
  CAPABILITY_BROWSER,
  CAPABILITY_DATABASE,
  CAPABILITY_MOBILE,
  CAPABILITY_SM,
  CAPABILITY_VM,
  CAPABILITY_WSM,
  ALARM_MAX_VALUE
} from '@/utils/constants';
import apm from '@/utils/templates/apm.template';
import browser from '@/utils/templates/browser.template';
import database from '@/utils/templates/database.template';
import mobile from '@/utils/templates/mobile.template';
import sm from '@/utils/templates/sm.template';
import vm from '@/utils/templates/vm.template';
import wsm from '@/utils/templates/wsm.template';

const InitAlarmDetails = {
  serviceId: '',
  config: '',
  email: '',
  slack: '',
  webhook: '',
  last_update: -1
};

const InitMetricsDetials = {
  metricsName: '',
  threshold: 0,
  period: 0,
  op: '',
  count: 0,
  silencePeriod: -1
};

const InitNotificationChannels = {
  serviceId: '',
  email: '',
  slack: '',
  webhook: '',
  last_update: -1
};

const AlarmCapabilities = [
  {
    key: CAPABILITY_APM,
    label: apm.name
  },
  {
    key: CAPABILITY_BROWSER,
    label: browser.name
  },
  {
    key: CAPABILITY_MOBILE,
    label: mobile.name
  },
  {
    key: CAPABILITY_VM,
    label: vm.name
  },
  {
    key: CAPABILITY_SM,
    label: sm.name
  },
  {
    key: CAPABILITY_DATABASE,
    label: database.name
  },
  {
    key: CAPABILITY_WSM,
    label: wsm.name
  }
];

const CapabilityType = new Map<String, String>();
CapabilityType.set(CAPABILITY_SM, 'S');
CapabilityType.set(CAPABILITY_BROWSER, 'B');
CapabilityType.set(CAPABILITY_APM, 'A');
CapabilityType.set(CAPABILITY_DATABASE, 'D');
CapabilityType.set(CAPABILITY_VM, 'V');
CapabilityType.set(CAPABILITY_MOBILE, 'M');
CapabilityType.set(CAPABILITY_WSM, 'W');

const InitOptions = {
  APM: {
    metrics: [
      {
        key: 'service_error_count',
        label: 'Error count'
      },
      {
        key: 'service_error_code',
        label: 'Error code',
        customMsg: 'Error count for code ',
        errorCodes: [
          '400',
          '401',
          '402',
          '403',
          '404',
          '405',
          '406',
          '407',
          '408',
          '409',
          '429',
          '500',
          '501',
          '502',
          '503',
          '504',
          '511'
        ]
      },
      {
        key: 'service_resp_time',
        label: 'Response time '
      },
      {
        key: 'service_sla',
        label: ' SLA '
      },
      {
        key: 'service_cpm',
        label: ' CPM'
      },
      {
        key: 'service_total_call',
        label: ' Load'
      },
      {
        key: 'service_apdex',
        label: ' Apdex',
        transform: value => {
          return value > 1 ? 0 : value * 10000;
        },
        displayValue: value => {
          return value / 10000;
        },
        info: 'Apdex should be between 0-1'
      },
      {
        key: 'apm_service_downtime',
        label: ' Service Down',
        displayValue: value => `${value} minute(s)`,
        info:
          'When ever there is no heat beat from any of the instances in the given period Service is considered as down',
        isServiceLevelAlert: true
      },
      {
        key: 'apm_service_instances_downtime',
        label: ' Service instances down',
        displayValue: (value) => `${value} minute(s)`,
        info:
          'When ever there is no heat beat from some of the instances in the given period Then that instance is considered as down.\n Note: There are some excepted false alerts triggered during each deployment.',
        isServiceLevelAlert: true
      }
    ]
  },
  BROWSER: {
    metrics: [
      {
        key: 'browser_app_apdex',
        label: 'Apdex',
        transform: value => {
          return value > 1 ? 0 : value * 10000;
        },
        displayValue: value => {
          return value / 10000;
        },
        info: 'Apdex should be between 0-1'
      },
      {
        key: 'browser_app_pv',
        label: 'Load'
      },
      {
        key: 'browser_app_resp_time',
        label: 'Load time'
      },
      {
        key: 'browser_app_error_sum',
        label: 'Error count'
      }
    ]
  },
  MOBILE: {
    metrics: [
      {
        key: 'mobile_app_crash_count',
        label: 'Total crashes'
      },
      {
        key: 'mobile_app_normal_count',
        label: 'Number of requests'
      },
      {
        key: 'mobile_app_perf_response_time',
        label: 'HTTP response time'
      }
    ]
  },
  VM: {
    metrics: [
      {
        key: 'meter_vm_cpu_total_percentage',
        label: 'CPU usage (%)',
        validation: {
          threshold: {
            min: 0,
            max: 10000
          }
        }
      },
      {
        key: 'meter_vm_memory_used',
        label: 'Memory used (bytes)'
      },
      {
        key: 'meter_vm_memory_available_percentage',
        label: 'Memory available (%)',
        validation: {
          threshold: {
            min: 0,
            max: 100
          }
        }
      },
      {
        key: 'meter_vm_memory_swap_free',
        label: 'Memory swap available (bytes)'
      },
      {
        key: 'meter_vm_memory_swap_percentage',
        label: 'Memory swap used (%)',
        validation: {
          threshold: {
            min: 0,
            max: 100
          }
        }
      },
      {
        key: 'meter_vm_filesystem_percentage_total_available',
        label: 'File system available (%)',
        validation: {
          threshold: {
            min: 0,
            max: 100
          }
        }
      },
      {
        key: 'meter_vm_disk_read',
        label: 'Disk reads (bytes/sec)'
      },
      {
        key: 'meter_vm_disk_written',
        label: 'Disk writes (bytes/sec)'
      },
      {
        key: 'meter_vm_network_receive',
        label: 'Network received (bytes/sec)'
      },
      {
        key: 'meter_vm_network_transmit',
        label: 'Network transmitted (bytes/sec)'
      },
      {
        key: 'meter_vm_network_total',
        label: 'Network total (bytes/sec)'
      },
      {
        key: 'vm_service_instances_downtime',
        label: 'VM instances down',
        displayValue: (value) => `${value} minute(s)`,
        validation: {
          period: {
            min: 4,
            max: ALARM_MAX_VALUE //Use this for NO MAX VALUE
          }
        },
        info:
          'When ever there is no heat beat from some of the instances in the given period Then that instance is considered as down.\n Note: There are some excepted false alerts triggered during restart of VM.',
        isServiceLevelAlert: true // reusing the same boolean defined for APM Service Alert
      }
    ]
  },
  SM: {
    metrics: [
      {
        key: 'browser_app_pv',
        label: 'Load'
      },
      {
        key: 'browser_app_resp_time',
        label: 'Load time'
      },
      {
        key: 'browser_app_error_sum',
        label: 'Error count'
      },
      {
        key: 'browser_app_page_ttfb_avg',
        label: 'Time to first byte'
      },
      {
        key: 'browser_app_page_dom_analysis_avg',
        label: 'DOM analysis time'
      },
      {
        key: 'browser_app_page_fpt_avg',
        label: 'First paint time'
      },
      {
        key: 'browser_app_page_load_page_avg',
        label: 'Page load time'
      },
      {
        key: 'browser_app_page_trans_avg',
        label: 'Content transfer time'
      },
      {
        key: 'browser_app_page_fmp_avg',
        label: 'First meaningful paint time'
      },
      {
        key: 'browser_app_page_tcp_avg',
        label: 'TCP time'
      },
      {
        key: 'browser_app_page_redirect_avg',
        label: 'Redirect time'
      },
      {
        key: 'browser_app_page_ttl_avg',
        label: 'Time to interact'
      }
    ]
  },
  EP: {
    metrics: [
      {
        key: 'meter_ep_resp_time',
        label: 'Response time'
      }, 
      {
        key: 'meter_ep_availability',
        label: 'Availability (%)',
        validation: {
          threshold: {
            min: 0,
            max: 100
          }
        }
      }]
  },
  DATABASE: {
    ES: {
      metrics: [
        {
          key: 'meter_cluster_nodes',
          label: 'Number of nodes'
        },
        {
          key: 'meter_shards_initializing',
          label: 'Number of initializing shards'
        },
        {
          key: 'meter_shards_unassigned',
          label: 'Number of unassigned shards'
        },
        {
          key: 'meter_primaries_queries_total',
          label: 'Total number of queries'
        },
        {
          key: 'meter_primaries_queries_in_milliseconds',
          label: 'Total time spent on queries (ms)'
        },
        {
          key: 'meter_primaries_query_active',
          label: 'Number of queries currently in progress'
        },
        {
          key: 'meter_primaries_query_fetches_total',
          label: 'Total number of fetches'
        },
        {
          key: 'meter_primaries_query_fetches_in_milliseconds',
          label: 'Total time spent on fetches (ms)'
        },
        {
          key: 'meter_primaries_query_fetches',
          label: 'Number of fetches currently in progress'
        },
        {
          key: 'meter_primaries_index_docs_total',
          label: 'Total number of documents indexed'
        },
        {
          key: 'meter_primaries_index_docs_currently_indexing_in_milliseconds',
          label: 'Total time spent indexing documents (ms)'
        },
        {
          key: 'meter_primaries_index_docs_currently_indexing',
          label: 'Number of documents currently being indexed'
        },
        {
          key: 'meter_primaries_index_refreshes_total',
          label: 'Total number of index refreshes'
        },
        {
          key: 'meter_primaries_index_refreshes_total_in_milliseconds',
          label: 'Total time spent refreshing indices (ms)'
        },
        {
          key: 'meter_primaries_flushes_total',
          label: 'Total number of index flushes to disk'
        },
        {
          key: 'meter_primaries_flush_total_time_in_milliseconds',
          label: 'Total time spent on flushing indices to disk (ms)'
        },
        {
          key: 'meter_primaries_get_documents_missing',
          label: 'Total number of GET requests where the document was missing'
        },
        {
          key: 'meter_primaries_get_documents_missing_in_milliseconds',
          label:
            'Total time spent on GET requests where the document was missing (ms)'
        },
        {
          key: 'meter_available_disk_space',
          label: 'Available disk space (bytes)'
        },
        {
          key: 'meter_cpu_percent',
          label: 'CPU utilization (%)',
          validation: {
            threshold: {
              min: 0,
              max: 100
            }
          }
        },
        {
          key: 'meter_memory_free_in_bytes',
          label: 'Memory free (bytes)'
        },
        {
          key: 'meter_memory_used_in_bytes',
          label: 'Memory used (bytes)'
        },
        {
          key: 'meter_io_total_read_operations',
          label: 'Total IO read operations'
        },
        {
          key: 'meter_io_total_write_operations',
          label: 'Total IO write operations'
        },
        {
          key: 'meter_transport_packets_received_in_bytes',
          label: 'Network bytes received'
        },
        {
          key: 'meter_transport_packets_sent_in_bytes',
          label: 'Network bytes sent'
        },
        {
          key: 'meter_open_f_d',
          label: 'Open file descriptors'
        },
        {
          key: 'meter_http_current_open_connections',
          label: 'Number of HTTP connections currently open'
        },
        {
          key: 'meter_http_opened_connections',
          label: 'Total number of HTTP connections opened over time'
        },
        {
          key: 'meter_threadpool_force_merge_queue',
          label: 'Threadpool merge queue'
        },
        {
          key: 'meter_jvm_gc_minor_collections_young_generation_objects',
          label: 'Total count of young-generation garbage collections'
        },
        {
          key:
            'meter_jvm_gc_minor_collections_young_generation_objects_in_milliseconds',
          label: 'Total time spent on young-generation garbage collections (ms)'
        },
        {
          key: 'meter_jvm_mem_heap_used',
          label: 'Heap memory used (%)',
          validation: {
            threshold: {
              min: 0,
              max: 100
            }
          }
        },
        {
          key: 'meter_jvm_mem_heap_used_in_bytes',
          label: 'Heap memory used (bytes)'
        }
      ]
    },
    CB: {
      metrics: [
        {
          key: 'meter_cluster_disk_free_in_bytes',
          label: 'Disk free (bytes)'
        },
        {
          key: 'meter_cluster_disk_used_in_bytes',
          label: 'Disk used (bytes)'
        },
        {
          key: 'meter_cluster_memory_used_in_bytes',
          label: 'Cluster memory used (bytes)'
        },
        {
          key: 'meter_node_cpu_utilization',
          label: 'CPU utilization (%)',
          validation: {
            threshold: {
              min: 0,
              max: 100
            }
          }
        },
        {
          key: 'meter_node_memory_used_in_bytes',
          label: 'Node memory used (bytes)'
        },
        {
          key: 'meter_node_memory_free_in_bytes',
          label: 'Node memory free (bytes)'
        },
        {
          key: 'meter_bucket_disk_fetches_per_second',
          label: 'Disk fetches per second'
        },
        {
          key: 'meter_bucket_disk_create_operations_per_second',
          label: 'Disk create operations per second'
        },
        {
          key: 'meter_bucket_couch_docs_fragmentation_percent',
          label: 'Docs fragmentation percent',
          validation: {
            threshold: {
              min: 0,
              max: 100
            }
          }
        },
        {
          key: 'meter_bucket_active_resident_items_ratio',
          label: 'Active resident items ratio'
        },
        {
          key: 'meter_bucket_cache_misses',
          label: 'Cache misses'
        },
        {
          key: 'meter_bucket_read_operations_per_second',
          label: 'Read operations per second'
        },
        {
          key: 'meter_bucket_write_operations_per_second',
          label: 'Write operations per second'
        },
        {
          key: 'meter_bucket_delete_hits_per_second',
          label: 'Delete hits per second'
        }
      ]
    },
    MY: {
      metrics: [
        {
          key: 'meter_db_innodb_buffer_pool_pages_data',
          label: 'Inno DB buffer pool pages data'
        },
        {
          key: 'meter_db_innodb_buffer_pool_pages_free',
          label: 'Inno DB buffer pool pages free'
        },
        {
          key: 'meter_db_innodb_row_lock_current_waits',
          label: 'Inno DB row lock current waits'
        },
        {
          key: 'meter_db_innodb_row_lock_time_avg',
          label: 'Inno DB row lock time average'
        },
        {
          key: 'meter_db_innodb_row_lock_waits_per_second',
          label: 'Inno DB row lock waits per second'
        },
        {
          key: 'meter_db_open_files',
          label: 'Total open files'
        },
        {
          key: 'meter_db_open_tables',
          label: 'Total open tables'
        },
        {
          key: 'meter_db_tables_locks_waited_per_second',
          label: 'Table locks waited per second'
        },
        {
          key: 'meter_net_connection_errors_max_connections_per_second',
          label: 'Max connection errors per sec'
        },
        {
          key: 'meter_net_max_used_connections',
          label: 'Max used connections'
        },
        {
          key: 'meter_query_com_insert_per_second',
          label: 'Insert queries per second'
        },
        {
          key: 'meter_query_com_select_per_second',
          label: 'Select queries per second'
        },
        {
          key: 'meter_query_prepared_stmt_count_per_second',
          label: 'Prepared stmt count per second'
        },
        {
          key: 'meter_query_queries_per_second',
          label: 'Queries per second'
        },
        {
          key: 'meter_query_slow_queries_per_second',
          label: 'Slow queries per second'
        },
        {
          key: 'meter_net_threads_connected',
          label: 'Total threads connected'
        },
        {
          key: 'meter_net_threads_running',
          label: 'Total threads running'
        }
      ]
    },
    MS: {
      metrics: [
        {
          key: 'meter_bufferpool_size_per_database_in_bytes',
          label: 'Buffer pool size per database (bytes)'
        },
        {
          key: 'meter_io_stall_in_milliseconds',
          label: 'IO stall in ms'
        },
        {
          key: 'meter_log_transaction_growth',
          label: 'Transaction log growth'
        },
        {
          key: 'meter_page_file_available',
          label: 'Page file available (bytes)'
        },
        {
          key: 'meter_page_file_total',
          label: 'Page file total (bytes)'
        },
        {
          key: 'meter_active_connections',
          label: 'Active connections'
        },
        {
          key: 'meter_memory_available',
          label: 'Memory available'
        },
        {
          key: 'meter_memory_total',
          label: 'Memory total'
        },
        {
          key: 'meter_memory_utilization',
          label: 'Memory utilization'
        },
        {
          key: 'meter_instance_background_processes_count',
          label: 'Background processes count'
        },
        {
          key: 'meter_instance_blocked_processes_count',
          label: 'Blocked processes count'
        },
        {
          key: 'meter_instance_disk_in_bytes',
          label: 'Disk size (bytes)'
        },
        {
          key: 'meter_instance_forced_parameterizations_per_second',
          label: 'Forced parameterizations per second'
        },
        {
          key: 'meter_instance_runnable_processes_count',
          label: 'Runnable processes count'
        },
        {
          key: 'meter_instance_runnable_tasks',
          label: 'Runnable tasks count'
        },
        {
          key: 'meter_instance_running_processes_count',
          label: 'Running processes count'
        },
        {
          key: 'meter_instance_sleeping_processes_count',
          label: 'Sleeping processes count'
        },
        {
          key: 'meter_instance_suspended_processes_count',
          label: 'Suspended processes count'
        },
        {
          key: 'meter_instance_transactions_per_second',
          label: 'Transactions per second'
        },
        {
          key: 'meter_stats_connections',
          label: 'User connections'
        },
        {
          key: 'meter_stats_deadlocks_per_second',
          label: 'Deadlocks per second'
        },
        {
          key: 'meter_stats_lock_waits_per_second',
          label: 'Lock waits per second'
        },
        {
          key: 'meter_stats_sql_compilations_per_second',
          label: 'SQL compilations per second'
        },
        {
          key: 'meter_stats_sql_recompilations_per_second',
          label: 'SQL recompilations per second'
        },
        {
          key: 'meter_stats_user_errors_per_second',
          label: 'User errors per second'
        },
        {
          key: 'meter_bufferpool_batch_requests_per_second',
          label: 'Bufferpool batch requests per second'
        },
        {
          key: 'meter_bufferpool_page_life_expectancy_in_milliseconds',
          label: 'Bufferpool page life expectancy in milliseconds'
        },
        {
          key: 'meter_bufferpool_size_in_bytes',
          label: 'Buffer pool size (bytes)'
        },
        {
          key: 'meter_system_buffer_pool_hit_percent',
          label: 'System buffer pool hit percent',
          validation: {
            threshold: {
              min: 0,
              max: 100
            }
          }
        },
        {
          key: 'meter_system_wait_time_in_milliseconds_per_second',
          label: 'System wait time in milliseconds per seconds'
        },
        {
          key: 'meter_buffer_checkpoint_pages_per_second',
          label: 'Buffer checkpoint pages per seconds'
        },
        {
          key: 'meter_access_page_splits_per_second',
          label: 'Access page splits per seconds'
        }
      ]
    },
    OR: {
      metrics: [
        {
          key: 'meter_oracle_db_cpu_usage_per_second',
          label: 'CPU usage per second'
        },
        {
          key: 'meter_oracle_db_host_cpu_utilization',
          label: 'Host cpu utilization (%)'
        },
        {
          key: 'meter_oracle_disk_read_time_in_milliseconds',
          label: 'Amount of file read time per second'
        },
        {
          key: 'meter_oracle_disk_write_time_in_milliseconds',
          label: 'Amount of file write time per second'
        },
        {
          key: 'meter_oracle_long_running_queries',
          label: 'Number of long running (> 60s) queries per second'
        },
        {
          key: 'meter_oracle_network_io_megabytes_per_second',
          label: 'I/O megabytes per Second'
        },
        {
          key: 'meter_oracle_network_io_requests_per_second',
          label: 'I/O requests per second'
        },
        {
          key: 'meter_oracle_query_transactions_per_second',
          label: 'User transaction per second'
        },
        {
          key: 'meter_oracle_redo_log_log_file_switch',
          label: 'Number of redo log file switch'
        },
        {
          key: 'meter_oracle_redo_log_waits',
          label: 'Number of redo log waits'
        },
        {
          key: 'meter_oracle_sga_hit_ratio',
          label: 'Hit ratio for the SGA'
        },
        {
          key: 'meter_oracle_sga_shared_pool_library_cache_shareable_memory_per_statement_in_bytes',
          label: 'SGA cacheable memory per statement in bytes'
        },
        {
          key: 'meter_oracle_sga_shared_pool_library_cache_shareable_memory_per_user_in_bytes',
          label: 'SGA cacheable memory per user in bytes'
        },
      ]
    }
  },
  WSM: {
    NG: {
      'open source': {
        metrics: [
          {
            key: 'meter_net_connections_accepted_per_second',
            label: 'Average accepted connections'
          },
          { key: 'meter_net_connections_active', label: 'Active connections' },
          {
            key: 'meter_net_connections_dropped_per_second',
            label: 'Average dropped connections'
          },
          {
            key: 'meter_net_connections_reading',
            label: 'Reading connections'
          },
          {
            key: 'meter_net_connections_waiting',
            label: 'Waiting connections'
          },
          {
            key: 'meter_net_connections_writing',
            label: 'Writing connections'
          },
          { key: 'meter_net_requests_per_second', label: 'Average requests' }
        ]
      },
      plus: {
        metrics: [
          {
            key: 'meter_net_connections_accepted_per_second',
            label: 'Average accepted connections'
          },
          { key: 'meter_net_connections_active', label: 'Active connections' },
          {
            key: 'meter_net_connections_dropped_per_second',
            label: 'Average dropped connections'
          },
          { key: 'meter_net_connections_idle', label: 'Idle connections' },
          { key: 'meter_net_requests', label: 'Requests' },
          { key: 'meter_net_requests_per_second', label: 'Average requests' },
          { key: 'meter_processes_respawned', label: 'Respawned processes' },
          {
            key: 'meter_ssl_failed_handshakes',
            label: 'Failed SSL handshakes'
          },
          { key: 'meter_ssl_handshakes', label: 'SSL handshakes' },
          { key: 'meter_ssl_session_reuses', label: 'SSL session reuses' }
        ]
      }
    }
  }
};

const InitopOptions = [
  {
    key: '>',
    label: ' Greater than  '
  },
  {
    key: '<',
    label: 'Less than '
  }
];

export {
  InitAlarmDetails,
  InitMetricsDetials,
  InitNotificationChannels,
  InitopOptions,
  InitOptions,
  CapabilityType,
  AlarmCapabilities
};
